import { useCallback, useMemo, useRef } from "react";
import { RowSelectionStore } from "../context/RowSelectionContext";

export const useCreateRowSelectionStore = (
  initialSelection?: string[] | ReadonlySet<string>,
): RowSelectionStore => {
  const selectionRef = useRef<Set<string>>(
    initialSelection ? new Set(initialSelection) : new Set(),
  );
  const revisionRef = useRef(0);
  const listenersRef = useRef<Set<() => void>>(new Set());

  const emitChange = useCallback(() => {
    revisionRef.current += 1;
    listenersRef.current.forEach((listener) => listener());
  }, []);

  const store = useMemo<RowSelectionStore>(() => {
    const getSelection = (): ReadonlySet<string> => selectionRef.current;
    const getRevision = () => revisionRef.current;

    const getSelectionCount = () => selectionRef.current.size;

    const subscribe = (listener: () => void) => {
      listenersRef.current.add(listener);
      return () => {
        listenersRef.current.delete(listener);
      };
    };

    const toggle = (id: string) => {
      if (selectionRef.current.has(id)) {
        selectionRef.current.delete(id);
      } else {
        selectionRef.current.add(id);
      }
      emitChange();
    };

    const toggleMany = (ids: string[]) => {
      const allSelected = ids.every((id) => selectionRef.current.has(id));
      if (allSelected) {
        ids.forEach((id) => selectionRef.current.delete(id));
      } else {
        ids.forEach((id) => selectionRef.current.add(id));
      }
      emitChange();
    };

    const clear = () => {
      if (selectionRef.current.size > 0) {
        selectionRef.current.clear();
        emitChange();
      }
    };

    const isSelected = (id: string) => selectionRef.current.has(id);

    return {
      getSelection,
      getRevision,
      getSelectionCount,
      subscribe,
      toggle,
      toggleMany,
      clear,
      isSelected,
    };
  }, [emitChange]);

  return store;
};
